import { z } from 'zod';
import { ServiceLineSchema } from './opportunities';
// ============================================================================
// P&L (Cuenta de Resultados)
// ============================================================================
export const PnlAccountRowSchema = z.object({
    /** Código PGC, p.ej. "700" o "640". */
    code: z.string(),
    /** Nombre legible de la cuenta. */
    name: z.string(),
    /** Saldo neto en céntimos. Positivo para ingresos, positivo para gastos. */
    amount: z.number().int(),
});
export const PnlSectionSchema = z.object({
    /** Título visible (p.ej. "Ingresos de explotación"). */
    title: z.string(),
    /** Identificador de bloque para estilado/agrupado en UI. */
    key: z.enum([
        'INGRESOS_EXPLOTACION',
        'APROVISIONAMIENTOS',
        'GASTOS_PERSONAL',
        'OTROS_GASTOS_EXPLOTACION',
        'AMORTIZACION_INMOVILIZADO',
        'RESULTADO_ENAJENACIONES',
        'RESULTADO_EXPLOTACION',
        'INGRESOS_FINANCIEROS',
        'GASTOS_FINANCIEROS',
        'RESULTADO_FINANCIERO',
        'RESULTADO_ANTES_IMPUESTOS',
        'IMPUESTO_SOCIEDADES',
        'RESULTADO_EJERCICIO',
    ]),
    /** Cuentas que componen el bloque (vacío si es solo subtotal). */
    accounts: z.array(PnlAccountRowSchema),
    /** Subtotal en céntimos. Signo según convención del bloque. */
    subtotal: z.number().int(),
});
export const PnlComparisonSchema = z.object({
    previousYear: z
        .object({
        finalResult: z.number().int(),
    })
        .nullable(),
});
export const PnlReportSchema = z.object({
    period: z.object({
        from: z.string(), // YYYY-MM-DD
        to: z.string(),
    }),
    serviceLine: ServiceLineSchema.nullable(),
    sections: z.array(PnlSectionSchema),
    /** Resultado del ejercicio (céntimos). */
    finalResult: z.number().int(),
    comparisons: PnlComparisonSchema,
});
export const PnlQuerySchema = z.object({
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    serviceLine: ServiceLineSchema.optional(),
});
// ============================================================================
// Balance de Situación
// ============================================================================
export const BalanceAccountRowSchema = z.object({
    code: z.string(),
    name: z.string(),
    /** Saldo acumulado en céntimos (signo natural: activos +, pasivos +, PN +). */
    amount: z.number().int(),
    /** Saldo del periodo anterior (céntimos) si se solicitó comparación. */
    previousAmount: z.number().int().nullable(),
});
export const BalanceSectionSchema = z.object({
    title: z.string(),
    key: z.enum([
        'ACTIVO_NO_CORRIENTE',
        'ACTIVO_CORRIENTE',
        'PASIVO_NO_CORRIENTE',
        'PASIVO_CORRIENTE',
        'PATRIMONIO_NETO',
    ]),
    accounts: z.array(BalanceAccountRowSchema),
    subtotal: z.number().int(),
    previousSubtotal: z.number().int().nullable(),
});
export const BalanceReportSchema = z.object({
    at: z.string(), // YYYY-MM-DD
    previousAt: z.string().nullable(),
    sections: z.array(BalanceSectionSchema),
    /** Total activo en céntimos. */
    totalActivo: z.number().int(),
    /** Pasivo + PN en céntimos. */
    totalPasivoPatrimonio: z.number().int(),
    /** Diferencia (activo - pasivo-pn). 0 si cuadra. */
    difference: z.number().int(),
    /** True si |difference| <= 1 céntimo. */
    balanced: z.boolean(),
    /**
     * Cuentas con saldo que no encajan en ningún bloque del balance. Deberían ser
     * siempre cero; si aparecen, explican EXACTAMENTE de dónde sale el descuadre
     * en vez de dejarlo como un misterio.
     */
    unclassifiedAccounts: z.array(z.string()).default([]),
});
export const BalanceQuerySchema = z.object({
    at: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    previousAt: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
});
//# sourceMappingURL=accounting-reports.js.map